import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PayActions } from './PayActions';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  X, Loader2, Plus, Trash2, Edit, Save, Upload, Paperclip,
  CalendarDays, DollarSign, Users, Wrench, FileText,
  TrendingUp, TrendingDown, ExternalLink, CheckCircle2, Target, UserPlus, Search, BookmarkPlus
} from 'lucide-react';
import { useConfirm } from './ConfirmDialog';
import EventVendorsPanel from './EventVendorsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- helpers ----------
const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_BADGE = {
  not_started: 'bg-slate-500/15 text-slate-400 border-slate-600',
  in_progress: 'bg-amber-500/15 text-amber-400 border-amber-600',
  blocked: 'bg-rose-500/15 text-rose-400 border-rose-600',
  done: 'bg-emerald-500/15 text-emerald-400 border-emerald-600',
};

const VENDOR_STATUS = {
  pending: 'bg-slate-500/15 text-slate-400',
  confirmed: 'bg-blue-500/15 text-blue-400',
  paid: 'bg-emerald-500/15 text-emerald-400',
  cancelled: 'bg-rose-500/15 text-rose-400',
};

// ---------- main modal ----------
export default function ProductionManager({ eventId, eventName, onClose, focusTaskId, embedded = false, extraTabs = [], initialTab = 'overview' }) {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState(initialTab);
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [budget, setBudget] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffRoster, setStaffRoster] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [s, t, b, v, st] = await Promise.all([
        axios.get(`${API}/events/${eventId}/production/summary`, auth),
        axios.get(`${API}/events/${eventId}/production/tasks`, auth),
        axios.get(`${API}/events/${eventId}/production/budget`, auth),
        axios.get(`${API}/events/${eventId}/production/vendors`, auth),
        axios.get(`${API}/events/${eventId}/production/staff`, auth),
      ]);
      setSummary(s.data);
      setTasks(t.data.tasks || []);
      setBudget(b.data.lines || []);
      setVendors(v.data.vendors || []);
      setStaff(st.data.staff || []);
      setStaffRoster(st.data.roster || []);
    } catch (e) {
      toast.error('Failed to load production data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => { refresh(); }, [refresh]);

  // Deep-link: when a focusTaskId is provided (from "Tasks across all events"
  // dashboard click), switch to the Calendar tab (where task rows are rendered),
  // scroll the matching task into view and flash a highlight.
  useEffect(() => {
    if (!focusTaskId) return;
    // Ensure the Calendar tab is active so the task-row element is mounted.
    setTab('calendar');
  }, [focusTaskId]);

  useEffect(() => {
    if (!focusTaskId || tasks.length === 0 || tab !== 'calendar') return;
    const tid = setTimeout(() => {
      const el = document.querySelector(`[data-testid="task-row-${focusTaskId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-fuchsia-500', 'transition');
        setTimeout(() => el.classList.remove('ring-2', 'ring-fuchsia-500'), 2600);
      }
    }, 250);
    return () => clearTimeout(tid);
  }, [focusTaskId, tasks, tab]);

  const tabsBody = (
    <Tabs value={tab} onValueChange={setTab} className="p-5">
      <TabsList className={`w-full ${extraTabs.length ? 'flex flex-wrap h-auto' : 'grid grid-cols-5'}`}>
        <TabsTrigger value="overview" data-testid="prod-tab-overview"><DollarSign className="h-3.5 w-3.5 mr-1" />Overview</TabsTrigger>
        <TabsTrigger value="calendar" data-testid="prod-tab-calendar"><CalendarDays className="h-3.5 w-3.5 mr-1" />Calendar</TabsTrigger>
        <TabsTrigger value="budget" data-testid="prod-tab-budget"><FileText className="h-3.5 w-3.5 mr-1" />Budget</TabsTrigger>
        <TabsTrigger value="vendors" data-testid="prod-tab-vendors"><Wrench className="h-3.5 w-3.5 mr-1" />Vendors</TabsTrigger>
        <TabsTrigger value="staff" data-testid="prod-tab-staff"><Users className="h-3.5 w-3.5 mr-1" />Staff</TabsTrigger>
        {extraTabs.map((t) => (
          <TabsTrigger key={t.value} value={t.value} data-testid={t.testId}>
            {t.icon && <t.icon className="h-3.5 w-3.5 mr-1" />}{t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="overview" className="mt-4">
        <OverviewPanel summary={summary} />
      </TabsContent>
      <TabsContent value="calendar" className="mt-4">
        <CalendarPanel eventId={eventId} tasks={tasks} refresh={refresh} auth={auth} />
      </TabsContent>
      <TabsContent value="budget" className="mt-4">
        <BudgetPanel eventId={eventId} lines={budget} summary={summary} refresh={refresh} auth={auth} token={token} />
      </TabsContent>
      <TabsContent value="vendors" className="mt-4">
        <EventVendorsPanel eventId={eventId} token={token} />
      </TabsContent>
      <TabsContent value="staff" className="mt-4">
        <StaffPanel staff={staff} roster={staffRoster} refresh={refresh} auth={auth} eventId={eventId} eventTitle={summary?.event?.title || summary?.event?.slug} />
      </TabsContent>
      {extraTabs.map((t) => (
        <TabsContent key={t.value} value={t.value} className="mt-4 space-y-3" data-testid={t.contentTestId}>
          {t.content}
        </TabsContent>
      ))}
    </Tabs>
  );

  // Embedded mode — render without modal chrome so the EventDashboard can
  // host Production inline inside its tab.
  if (embedded) {
    if (loading) {
      return (
        <div className="bg-card rounded-xl border border-border p-12 flex items-center justify-center" data-testid="production-manager-embedded">
          <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
        </div>
      );
    }
    return (
      <div className="bg-card rounded-xl border border-border" data-testid="production-manager-embedded">
        {tabsBody}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="w-full max-w-6xl my-6 bg-card rounded-xl border border-border shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="production-manager-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Wrench className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Production Manager</h2>
              <p className="text-xs text-muted-foreground">{eventName}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-production">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
          </div>
        ) : tabsBody}
      </div>
    </div>
  );
}

// ===========================================================================
// OVERVIEW
// ===========================================================================
function OverviewPanel({ summary }) {
  if (!summary) return null;
  const { actuals_pulled: a, totals: t, counts: c, breakeven: be, cac } = summary;
  const netClass = (t.actual_net_cents >= 0) ? 'text-emerald-400' : 'text-rose-400';
  return (
    <div className="space-y-4" data-testid="production-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI icon={TrendingUp} label="Actual Income" value={fmt(t.actual_income_cents)} color="emerald" testid="kpi-actual-income" />
        <KPI icon={TrendingDown} label="Actual Expense" value={fmt(t.actual_expense_cents)} color="rose" testid="kpi-actual-expense" />
        <KPI icon={DollarSign} label="Actual Net" value={fmt(t.actual_net_cents)} color={t.actual_net_cents >= 0 ? 'emerald' : 'rose'} testid="kpi-actual-net" />
        <KPI icon={DollarSign} label="Forecast Net" value={fmt(t.estimated_net_cents)} color="indigo" testid="kpi-est-net" />
      </div>

      {/* Breakeven + CAC */}
      <div className="grid sm:grid-cols-2 gap-3">
        <BreakevenCard be={be || {}} />
        <CACCard cac={cac || {}} />
      </div>

      <Card className="border-border">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-sm font-semibold">Live Actuals (auto-pulled)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <Stat label="Tickets revenue" value={fmt(a.tickets_revenue_cents)} sub={`${a.tickets_sold} tickets · ${a.orders_count} orders`} />
            <Stat label="Sponsorship revenue" value={fmt(a.sponsorship_revenue_cents)} sub={`${a.sponsorship_count} paid sponsors`} />
            <Stat label="Street team payout" value={fmt(a.street_team_payout_cents)} sub="Promo partner credits" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-3">Forecast vs Actual</h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Stat label="Est. Income" value={fmt(t.estimated_income_cents)} />
            <Stat label="Est. Expense" value={fmt(t.estimated_expense_cents)} />
            <Stat label="Est. Net" value={fmt(t.estimated_net_cents)} accent={t.estimated_net_cents >= 0 ? 'text-emerald-400' : 'text-rose-400'} />
            <Stat label="Actual Income" value={fmt(t.actual_income_cents)} />
            <Stat label="Actual Expense" value={fmt(t.actual_expense_cents)} />
            <Stat label="Actual Net" value={fmt(t.actual_net_cents)} accent={netClass} />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-3 gap-3">
        <MiniCount label="Tasks" value={`${c.tasks_done}/${c.tasks}`} sub="completed" />
        <MiniCount label="Vendors" value={c.vendors} sub="locked in" />
        <MiniCount label="Sponsors paid" value={a.sponsorship_count} sub="live" />
      </div>
    </div>
  );
}

function BreakevenCard({ be }) {
  const target = be.target_cents || 0;
  const revenue = be.revenue_to_date_cents || 0;
  const pct = Math.min(100, be.progress_pct || 0);
  const reached = !!be.is_breakeven_reached;
  const gap = be.gap_cents || 0;
  const tix = be.tickets_needed_to_breakeven || 0;
  const avgTix = be.avg_ticket_price_cents || 0;
  const avgIsForecast = !!be.avg_ticket_is_forecast;
  const barColor = reached ? 'bg-emerald-500' : pct >= 75 ? 'bg-amber-500' : 'bg-indigo-500';
  return (
    <Card className="border-border" data-testid="breakeven-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-indigo-500/10 flex items-center justify-center">
              <Target className="h-4 w-4 text-indigo-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Breakeven</h3>
              <p className="text-[10px] text-muted-foreground">
                {avgIsForecast && !reached
                  ? 'Estimated from forecast · updates with real sales'
                  : 'Revenue needed to cover expenses'}
              </p>
            </div>
          </div>
          {reached ? (
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-400" data-testid="breakeven-reached">Reached</span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{pct}%</span>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Revenue · {fmt(revenue)}</span>
            <span className="font-medium">Target · {fmt(target)}</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full ${barColor} transition-all`}
              style={{ width: `${pct}%` }}
              data-testid="breakeven-bar"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border text-sm">
          {reached ? (
            <Stat label="Surplus over breakeven" value={fmt(revenue - target)} accent="text-emerald-400" />
          ) : (
            <Stat label="Still needed" value={fmt(gap)} accent={gap > 0 ? 'text-rose-400' : ''} />
          )}
          <Stat
            label={reached ? 'Avg ticket price' : (avgIsForecast ? 'Tickets to breakeven (est.)' : 'Tickets to breakeven')}
            value={reached ? fmt(avgTix) : (tix > 0 ? `${tix.toLocaleString()}` : '—')}
            sub={!reached && avgTix > 0
              ? `@ ${fmt(avgTix)} ${avgIsForecast ? 'forecast' : 'avg'}`
              : (avgTix === 0 && !reached ? 'Add a ticket forecast to estimate' : null)}
          />
        </div>
        {avgIsForecast && !reached && tix > 0 && (
          <div className="text-[10px] text-indigo-400/90 bg-indigo-500/5 border border-indigo-500/20 rounded px-2 py-1.5" data-testid="breakeven-forecast-note">
            Forecast-based estimate · fill actual ticket sales to refine.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CACCard({ cac }) {
  const spend = cac.marketing_spend_cents || 0;
  const estimated = cac.marketing_lines_estimated_cents || 0;
  const actualLines = cac.marketing_lines_actual_cents || 0;
  const customers = cac.unique_customers || 0;
  const forecastCustomers = cac.forecast_customers || 0;
  const effectiveCustomers = cac.effective_customers || 0;
  const cacCents = cac.cac_cents || 0;
  const street = cac.street_team_cents || 0;
  const streetEst = cac.street_team_estimated_cents || 0;
  const isForecast = !!cac.cac_is_forecast;
  const streetShown = Math.max(street, streetEst);
  const lineShown = Math.max(actualLines, estimated);
  const showCAC = effectiveCustomers > 0 && spend > 0;
  return (
    <Card className="border-border" data-testid="cac-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <UserPlus className="h-4 w-4 text-amber-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">CAC · Cost to Acquire</h3>
              <p className="text-[10px] text-muted-foreground">
                {isForecast ? 'Forecast: marketing budget ÷ projected attendance' : 'Marketing spend ÷ unique customers'}
              </p>
            </div>
          </div>
          <span className="text-base font-bold tabular-nums" data-testid="cac-value">
            {showCAC ? fmt(cacCents) : '—'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border text-sm">
          <Stat
            label={isForecast ? 'Marketing budget' : 'Marketing spend'}
            value={fmt(spend)}
            sub={`${fmt(lineShown)} budget · ${fmt(streetShown)} street team`}
          />
          <Stat
            label={isForecast ? 'Forecast attendance' : 'Customers acquired'}
            value={effectiveCustomers.toLocaleString()}
            sub={isForecast
              ? `from ticket forecast · ${customers} actual so far`
              : 'unique buyer emails'}
          />
        </div>

        {isForecast && spend > 0 && (
          <div className="text-[10px] text-amber-400/90 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5" data-testid="cac-forecast-note">
            Forecast-based estimate · updates once sales come in.
          </div>
        )}

        <p className="text-[10px] text-muted-foreground">
          Tag any expense line "Marketing", "Ads", "Promo", or "Campaign" and it'll auto-include in spend.
        </p>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// CALENDAR (TASKS)
// ===========================================================================
function CalendarPanel({ eventId, tasks, refresh, auth }) {
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyTask());
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  function emptyTask() {
    return { name: '', start_date: todayISO(), due_date: todayISO(), budget_cents: 0, responsible: '', status: 'not_started', notes: '' };
  }

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Task name required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/events/${eventId}/production/tasks/${editingId}`, form, auth);
        toast.success('Task updated');
      } else {
        await axios.post(`${API}/events/${eventId}/production/tasks`, form, auth);
        toast.success('Task added');
      }
      setForm(emptyTask()); setAdding(false); setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleEdit = (t) => {
    setEditingId(t.task_id);
    setAdding(true);
    setForm({
      name: t.name || '',
      start_date: t.start_date || todayISO(),
      due_date: t.due_date || '',
      budget_cents: t.budget_cents || 0,
      responsible: t.responsible || '',
      status: t.status || 'not_started',
      notes: t.notes || '',
    });
  };

  const handleDelete = async (taskId) => {
    if (!(await confirm({ title: 'Delete this task?', confirmText: 'Delete' }))) return;
    try {
      await axios.delete(`${API}/events/${eventId}/production/tasks/${taskId}`, auth);
      toast.success('Task deleted');
      refresh();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-3" data-testid="production-calendar">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Production Calendar ({tasks.length})</h3>
        {!adding && (
          <Button size="sm" onClick={() => { setAdding(true); setEditingId(null); setForm(emptyTask()); }} data-testid="add-task-btn">
            <Plus className="h-4 w-4 mr-1" />Add task
          </Button>
        )}
      </div>

      {adding && (
        <Card className="border-amber-500/40">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Task name *">
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="task-name-input" />
              </Field>
              <Field label="Responsible">
                <Input value={form.responsible} onChange={e => setForm({ ...form, responsible: e.target.value })} data-testid="task-responsible-input" />
              </Field>
              <Field label="Start date">
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="task-start-input" />
              </Field>
              <Field label="Due date">
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input" />
              </Field>
              <Field label="Budget (USD)">
                <Input
                  type="number" step="0.01" min="0"
                  value={(form.budget_cents || 0) / 100}
                  onChange={e => setForm({ ...form, budget_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                  data-testid="task-budget-input"
                />
              </Field>
              <Field label="Status">
                <select
                  className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
                  value={form.status}
                  onChange={e => setForm({ ...form, status: e.target.value })}
                  data-testid="task-status-select"
                >
                  <option value="not_started">Not started</option>
                  <option value="in_progress">In progress</option>
                  <option value="blocked">Blocked</option>
                  <option value="done">Done</option>
                </select>
              </Field>
            </div>
            <Field label="Notes">
              <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="task-notes-input" />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setEditingId(null); }}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-task-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                {editingId ? 'Update' : 'Save'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="tasks-table">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">Task</th>
              <th className="px-3 py-2 text-left">Owner</th>
              <th className="px-3 py-2 text-left">Start</th>
              <th className="px-3 py-2 text-left">Due</th>
              <th className="px-3 py-2 text-right">Budget</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr><td colSpan="7" className="px-3 py-6 text-center text-muted-foreground text-xs">No tasks yet — add your first day-of production task above.</td></tr>
            ) : tasks.map(t => (
              <tr key={t.task_id} className="border-t border-border" data-testid={`task-row-${t.task_id}`}>
                <td className="px-3 py-2 font-medium">{t.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{t.responsible || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{t.start_date || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{t.due_date || '—'}</td>
                <td className="px-3 py-2 text-right">{fmt(t.budget_cents)}</td>
                <td className="px-3 py-2">
                  <Badge className={`${STATUS_BADGE[t.status] || ''} border text-[10px]`}>{(t.status || '').replace('_', ' ')}</Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="icon" variant="ghost" onClick={() => handleEdit(t)} data-testid={`edit-task-${t.task_id}`}><Edit className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => handleDelete(t.task_id)} data-testid={`delete-task-${t.task_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// BUDGET
// ===========================================================================
function BudgetPanel({ eventId, lines, summary, refresh, auth, token }) {
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyLine());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [forecastOpen, setForecastOpen] = useState(false);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  function emptyLine() {
    return { line_type: 'expense', category: 'General', name: '', estimated_cents: 0, actual_cents: 0, notes: '' };
  }

  const incomeLines = lines.filter(l => l.line_type === 'income');
  const expenseLines = lines.filter(l => l.line_type === 'expense');

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Line name required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/events/${eventId}/production/budget/${editingId}`, form, auth);
        toast.success('Line updated');
      } else {
        await axios.post(`${API}/events/${eventId}/production/budget`, form, auth);
        toast.success('Line added');
      }
      setForm(emptyLine()); setAdding(false); setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleEdit = (l) => {
    setEditingId(l.line_id);
    setAdding(true);
    setForm({
      line_type: l.line_type, category: l.category || 'General', name: l.name,
      estimated_cents: l.estimated_cents || 0, actual_cents: l.actual_cents || 0, notes: l.notes || '',
    });
  };

  const handleDelete = async (lineId) => {
    if (!(await confirm({ title: 'Delete this line?', confirmText: 'Delete' }))) return;
    try {
      await axios.delete(`${API}/events/${eventId}/production/budget/${lineId}`, auth);
      toast.success('Deleted');
      refresh();
    } catch { toast.error('Delete failed'); }
  };

  const handleUploadClick = (lineId) => {
    uploadTargetRef.current = lineId;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const lineId = uploadTargetRef.current;
    if (!file || !lineId) return;
    setUploadingId(lineId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const url = `${API}/files/${up.data.storage_path}`;
      await axios.post(`${API}/events/${eventId}/production/budget/${lineId}/attach`, {
        url, filename: file.name, kind: 'receipt'
      }, auth);
      toast.success('Receipt attached');
      refresh();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Upload failed');
    } finally { setUploadingId(null); }
  };

  const detach = async (lineId, idx) => {
    if (!(await confirm({ title: 'Remove this attachment?', confirmText: 'Remove' }))) return;
    try {
      await axios.delete(`${API}/events/${eventId}/production/budget/${lineId}/attach/${idx}`, auth);
      refresh();
    } catch { toast.error('Remove failed'); }
  };

  const patchAttachment = async (lineId, idx, patch) => {
    try {
      await axios.patch(`${API}/events/${eventId}/production/budget/${lineId}/attach/${idx}`, patch, auth);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update receipt');
    }
  };

  const liveActuals = summary?.actuals_pulled || {};
  const liveTargets = summary?.live_targets || {};

  const saveTarget = async (key, valueCents) => {
    try {
      await axios.patch(`${API}/events/${eventId}/production/budget-targets`, { [key]: Math.max(0, valueCents) }, auth);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save estimate');
    }
  };

  return (
    <div className="space-y-4" data-testid="production-budget">
      <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFileSelected} data-testid="budget-receipt-input" />

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Pro-forma Budget</h3>
        {!adding && (
          <Button size="sm" onClick={() => { setAdding(true); setEditingId(null); setForm(emptyLine()); }} data-testid="add-budget-line-btn">
            <Plus className="h-4 w-4 mr-1" />Add line
          </Button>
        )}
      </div>

      {adding && (
        <Card className="border-amber-500/40">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Type">
                <select
                  className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
                  value={form.line_type}
                  onChange={e => setForm({ ...form, line_type: e.target.value })}
                  data-testid="budget-type-select"
                >
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                </select>
              </Field>
              <Field label="Category">
                <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g. Production, Marketing" data-testid="budget-category-input" />
              </Field>
              <Field label="Line item *">
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="budget-name-input" />
              </Field>
              <Field label="Estimated (USD)">
                <Input
                  type="number" step="0.01" min="0"
                  value={(form.estimated_cents || 0) / 100}
                  onChange={e => setForm({ ...form, estimated_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                  data-testid="budget-est-input"
                />
              </Field>
              <Field label="Actual (USD)">
                <Input
                  type="number" step="0.01" min="0"
                  value={(form.actual_cents || 0) / 100}
                  onChange={e => setForm({ ...form, actual_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                  data-testid="budget-actual-input"
                />
              </Field>
            </div>
            <Field label="Notes">
              <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setEditingId(null); }}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-budget-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                {editingId ? 'Update' : 'Save'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <BudgetSection
        title="Income"
        accent="emerald"
        rows={incomeLines}
        liveRows={[
          { key: 'tickets_estimated_cents', name: 'Ticket sales (live)', actual_cents: liveActuals.tickets_revenue_cents || 0, estimated_cents: liveTargets.tickets_estimated_cents || 0, sub: `${liveActuals.tickets_sold || 0} tickets sold`, forecastable: true },
          { key: 'sponsorships_estimated_cents', name: 'Sponsorships (live)', actual_cents: liveActuals.sponsorship_revenue_cents || 0, estimated_cents: liveTargets.sponsorships_estimated_cents || 0, sub: `${liveActuals.sponsorship_count || 0} paid sponsors` },
        ]}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onUpload={handleUploadClick}
        onDetach={detach}
        onPatchAttachment={patchAttachment}
        onSaveTarget={saveTarget}
        onForecast={() => setForecastOpen(true)}
        uploadingId={uploadingId}
      />

      <BudgetSection
        title="Expenses"
        accent="rose"
        rows={expenseLines}
        liveRows={[
          { key: 'street_team_estimated_cents', name: 'Street team payouts (live)', actual_cents: liveActuals.street_team_payout_cents || 0, estimated_cents: liveTargets.street_team_estimated_cents || 0, sub: 'Promo partner credits earned' },
        ]}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onUpload={handleUploadClick}
        onDetach={detach}
        onPatchAttachment={patchAttachment}
        onSaveTarget={saveTarget}
        uploadingId={uploadingId}
      />

      {forecastOpen && (
        <TicketForecastModal
          eventId={eventId}
          auth={auth}
          onClose={() => setForecastOpen(false)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

function BudgetSection({ title, accent, rows, liveRows, onEdit, onDelete, onUpload, onDetach, onPatchAttachment, onSaveTarget, onForecast, uploadingId }) {
  const accentClass = accent === 'emerald' ? 'text-emerald-400' : 'text-rose-400';
  const totalEst = rows.reduce((s, r) => s + (r.estimated_cents || 0), 0)
                    + liveRows.reduce((s, r) => s + (r.estimated_cents || 0), 0);
  const totalActManual = rows.reduce((s, r) => s + (r.actual_cents || 0), 0);
  const totalLive = liveRows.reduce((s, r) => s + (r.actual_cents || 0), 0);
  const totalAct = totalActManual + totalLive;

  return (
    <Card className="border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className={`text-sm font-semibold ${accentClass}`}>{title}</h4>
          <span className="text-xs text-muted-foreground">Est {fmt(totalEst)} · Actual {fmt(totalAct)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-xs">
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Line item</th>
                <th className="px-3 py-2 text-right">Estimated</th>
                <th className="px-3 py-2 text-right">Actual</th>
                <th className="px-3 py-2 text-left">Receipts</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {liveRows.map((r, i) => (
                <tr key={`live-${i}`} className="border-t border-border bg-muted/20">
                  <td className="px-3 py-2 text-xs text-muted-foreground italic">Auto</td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{r.name}</span>
                    <div className="text-[10px] text-muted-foreground">{r.sub}</div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.forecastable && onForecast ? (
                      <button
                        type="button"
                        onClick={onForecast}
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400 hover:text-emerald-300 hover:underline"
                        data-testid="open-ticket-forecast"
                      >
                        {(r.estimated_cents || 0) > 0 ? fmt(r.estimated_cents) : 'Forecast →'}
                      </button>
                    ) : r.key && onSaveTarget ? (
                      <LiveTargetInput
                        targetKey={r.key}
                        valueCents={r.estimated_cents || 0}
                        onSave={onSaveTarget}
                      />
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-medium">{fmt(r.actual_cents)}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">—</td>
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground">live</td>
                </tr>
              ))}
              {rows.length === 0 && liveRows.length === 0 && (
                <tr><td colSpan="6" className="px-3 py-6 text-center text-muted-foreground text-xs">No {title.toLowerCase()} yet.</td></tr>
              )}
              {rows.map(l => {
                const atts = l.attachments || [];
                const sumReceipts = atts.reduce((s, a) => s + (a.amount_cents || 0), 0);
                const fromReceipts = !!l.actual_from_receipts;
                return (
                <tr key={l.line_id} className="border-t border-border" data-testid={`budget-row-${l.line_id}`}>
                  <td className="px-3 py-2 text-xs">{l.category}</td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{l.name}</span>
                    {l.notes && <div className="text-[10px] text-muted-foreground truncate max-w-[260px]">{l.notes}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">{fmt(l.estimated_cents)}</td>
                  <td className="px-3 py-2 text-right">
                    {fmt(l.actual_cents)}
                    {fromReceipts && atts.length > 0 && (
                      <div className="text-[9px] text-emerald-400 leading-tight">{atts.length} receipt{atts.length === 1 ? '' : 's'}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {atts.length === 0 ? (
                      <span className="text-[10px] text-muted-foreground">No files</span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {atts.map((a, i) => (
                          <ReceiptRow
                            key={i}
                            attachment={a}
                            lineId={l.line_id}
                            idx={i}
                            onDetach={onDetach}
                            onAmountChange={onPatchAttachment}
                          />
                        ))}
                        {fromReceipts && atts.length > 1 && (
                          <div className="flex items-center justify-between text-[10px] font-semibold text-emerald-400 pt-0.5 mt-0.5 border-t border-border">
                            <span>Total</span>
                            <span data-testid={`receipts-total-${l.line_id}`}>{fmt(sumReceipts)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={() => onUpload(l.line_id)} disabled={uploadingId === l.line_id} title="Attach receipt" data-testid={`upload-${l.line_id}`}>
                      {uploadingId === l.line_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => onEdit(l)} data-testid={`edit-budget-${l.line_id}`}><Edit className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => onDelete(l.line_id)} data-testid={`delete-budget-${l.line_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// VENDORS
// ===========================================================================
function VendorsPanel({ eventId, vendors, refresh, auth }) {
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyVendor());
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  function emptyVendor() {
    return { name: '', role: '', contact_name: '', contact_email: '', contact_phone: '', fee_cents: 0, status: 'pending', notes: '', source_supplier_id: null };
  }

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Vendor name required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/events/${eventId}/production/vendors/${editingId}`, form, auth);
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API}/events/${eventId}/production/vendors`, form, auth);
        toast.success('Vendor added');
      }
      setForm(emptyVendor()); setAdding(false); setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleEdit = (v) => {
    setEditingId(v.vendor_id);
    setAdding(true);
    setForm({
      name: v.name, role: v.role || '', contact_name: v.contact_name || '',
      contact_email: v.contact_email || '', contact_phone: v.contact_phone || '',
      fee_cents: v.fee_cents || 0, status: v.status || 'pending', notes: v.notes || '',
      source_supplier_id: v.source_supplier_id || null,
    });
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Delete vendor?', confirmText: 'Delete' }))) return;
    try { await axios.delete(`${API}/events/${eventId}/production/vendors/${id}`, auth); refresh(); }
    catch { toast.error('Delete failed'); }
  };

  const handlePickSupplier = (s) => {
    // Pre-fill the form with the chosen supplier's data, leaving fee/notes blank for the admin.
    setForm({
      name: s.business_name || s.name || 'Vendor',
      role: s.category || '',
      contact_name: s.contact_name || s.owner_name || '',
      contact_email: s.contact_email || s.email || '',
      contact_phone: s.contact_phone || s.phone || '',
      fee_cents: 0,
      status: 'pending',
      notes: s.tagline || s.description ? `From Supplier Hub: ${s.tagline || (s.description || '').slice(0, 200)}` : 'From Supplier Hub',
      source_supplier_id: s.vendor_id || s.id || null,
    });
    setEditingId(null);
    setAdding(true);
    setPickerOpen(false);
    toast.success(`Loaded ${s.business_name || 'supplier'} — review & save`);
  };

  return (
    <div className="space-y-3" data-testid="production-vendors">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Vendors ({vendors.length})</h3>
        {!adding && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="pick-from-hub-btn">
              <BookmarkPlus className="h-4 w-4 mr-1" />Pick from Supplier Hub
            </Button>
            <Button size="sm" onClick={() => { setAdding(true); setEditingId(null); setForm(emptyVendor()); }} data-testid="add-vendor-btn">
              <Plus className="h-4 w-4 mr-1" />Add vendor
            </Button>
          </div>
        )}
      </div>

      {pickerOpen && (
        <SupplierHubPicker onPick={handlePickSupplier} onClose={() => setPickerOpen(false)} />
      )}

      {adding && (
        <Card className="border-amber-500/40">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Vendor name *"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" /></Field>
              <Field label="Role / service"><Input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="e.g. Catering, A/V, Security" data-testid="vendor-role-input" /></Field>
              <Field label="Contact name"><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></Field>
              <Field label="Contact email"><Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} /></Field>
              <Field label="Contact phone"><Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} /></Field>
              <Field label="Fee (USD)">
                <Input type="number" step="0.01" min="0"
                  value={(form.fee_cents || 0) / 100}
                  onChange={e => setForm({ ...form, fee_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                  data-testid="vendor-fee-input"
                />
              </Field>
              <Field label="Status">
                <select className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
                  value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="vendor-status-select">
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </Field>
            </div>
            <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setEditingId(null); }}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-vendor-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                {editingId ? 'Update' : 'Save'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="vendors-table">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Contact</th>
              <th className="px-3 py-2 text-right">Fee</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {vendors.length === 0 ? (
              <tr><td colSpan="6" className="px-3 py-6 text-center text-muted-foreground text-xs">No vendors yet.</td></tr>
            ) : vendors.map(v => (
              <tr key={v.vendor_id} className="border-t border-border" data-testid={`vendor-row-${v.vendor_id}`}>
                <td className="px-3 py-2 font-medium">{v.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{v.role || '—'}</td>
                <td className="px-3 py-2 text-xs">
                  {v.contact_name && <div>{v.contact_name}</div>}
                  {v.contact_email && <div className="text-muted-foreground">{v.contact_email}</div>}
                  {v.contact_phone && <div className="text-muted-foreground">{v.contact_phone}</div>}
                </td>
                <td className="px-3 py-2 text-right">{fmt(v.fee_cents)}</td>
                <td className="px-3 py-2">
                  <Badge className={`${VENDOR_STATUS[v.status] || ''} text-[10px]`}>{v.status}</Badge>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => handleEdit(v)} data-testid={`edit-vendor-${v.vendor_id}`}><Edit className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => handleDelete(v.vendor_id)} data-testid={`delete-vendor-${v.vendor_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// STAFF (read-only proxy)
// ===========================================================================
function StaffPanel({ staff, eventId, eventTitle, roster = [], refresh, auth }) {
  const [form, setForm] = useState({ name: '', role: '', email: '', phone: '', pay: '', notes: '' });
  const [adding, setAdding] = useState(false);
  const addStaff = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setAdding(true);
    try {
      await axios.post(`${API}/events/${eventId}/production/staff/direct`, {
        name: form.name, role: form.role, email: form.email, phone: form.phone,
        pay_cents: Math.round(parseFloat(form.pay || 0) * 100), notes: form.notes,
      }, auth);
      toast.success(`${form.name} added to staff`);
      setForm({ name: '', role: '', email: '', phone: '', pay: '', notes: '' });
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add staff'); }
    finally { setAdding(false); }
  };
  const removeStaff = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the staff roster?`)) return;
    try { await axios.delete(`${API}/production/staff/direct/${s.staff_id}`, auth); refresh && refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
  };
  const postUrl = `/staffing?post=1&event_id=${encodeURIComponent(eventId || '')}${eventTitle ? `&event_name=${encodeURIComponent(eventTitle)}` : ''}`;
  return (
    <div className="space-y-5" data-testid="production-staff">
      {/* Direct roster — add staff like vendors */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Staff roster ({roster.length})</h3>
        <div className="rounded-lg border border-border p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="staff-add-form">
          <input className="h-8 rounded-md border border-border bg-background px-2 text-xs" placeholder="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="staff-add-name" />
          <input className="h-8 rounded-md border border-border bg-background px-2 text-xs" placeholder="Role (e.g. Security)" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} data-testid="staff-add-role" />
          <input className="h-8 rounded-md border border-border bg-background px-2 text-xs" placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="staff-add-email" />
          <input className="h-8 rounded-md border border-border bg-background px-2 text-xs" placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="staff-add-phone" />
          <input className="h-8 rounded-md border border-border bg-background px-2 text-xs" type="number" min="0" placeholder="Pay $" value={form.pay} onChange={e => setForm(f => ({ ...f, pay: e.target.value }))} data-testid="staff-add-pay" />
          <button onClick={addStaff} disabled={adding} className="h-8 rounded-md bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold" data-testid="staff-add-submit">{adding ? '…' : '+ Add staff'}</button>
        </div>
        {roster.length > 0 && (
          <div className="border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm" data-testid="staff-roster-table">
              <thead className="bg-muted/50"><tr>
                <th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Role</th>
                <th className="px-3 py-2 text-left">Contact</th><th className="px-3 py-2 text-right">Agreed pay</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2 text-right"></th>
              </tr></thead>
              <tbody>
                {roster.map(s => (
                  <tr key={s.staff_id} className="border-t border-border/60">
                    <td className="px-3 py-1.5 font-medium">{s.name}</td>
                    <td className="px-3 py-1.5">{s.role || '—'}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">{[s.email, s.phone].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="px-3 py-1.5 text-right">{s.pay_cents ? `$${(s.pay_cents / 100).toFixed(2)}` : '—'}</td>
                    <td className="px-3 py-1.5 text-right text-emerald-400">{s.paid_cents ? `$${(s.paid_cents / 100).toFixed(2)}` : '—'}</td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      <PayActions eventId={eventId} payeeType="staff" refId={s.staff_id} name={s.name}
                        email={s.email} agreedCents={s.pay_cents} paidCents={s.paid_cents} onDone={refresh} />
                      <button onClick={() => removeStaff(s)} className="ml-2 text-[11px] text-rose-400 hover:underline" data-testid={`staff-remove-${s.staff_id}`}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">Pay staff straight to their bank from your Eventism wallet — send them a bank link once, then hit Pay.</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">Job postings ({staff.length})</h3>
        <div className="flex items-center gap-2">
          <a
            href={postUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold px-3 py-1.5"
            data-testid="post-job-for-event"
          >
            <Plus className="h-3.5 w-3.5" /> Post job for this event
          </a>
          <a href="/staffing" target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:underline flex items-center gap-1" data-testid="open-staffing-portal">
            Portal <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="staff-table">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">Position</th>
              <th className="px-3 py-2 text-left">Date / Time</th>
              <th className="px-3 py-2 text-left">Slots</th>
              <th className="px-3 py-2 text-left">Assigned</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 ? (
              <tr><td colSpan="6" className="px-3 py-6 text-center text-muted-foreground text-xs">No staffing jobs posted for this event yet. <a href={postUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline" data-testid="post-staffing-empty">Post one →</a></td></tr>
            ) : staff.map(j => (
              <tr key={j.job_id} className="border-t border-border" data-testid={`staff-row-${j.job_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{j.position_label || j.title}</div>
                  <div className="text-[10px] text-muted-foreground uppercase">{j.position_type}{j.is_custom_position ? ' · custom' : ''}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div>{j.date}</div>
                  <div className="text-muted-foreground">{j.start_time} – {j.end_time}</div>
                </td>
                <td className="px-3 py-2">{j.slots_filled || 0}/{j.slots_needed}</td>
                <td className="px-3 py-2 text-xs">
                  {(j.assigned_staff && j.assigned_staff.length) ? (
                    <div className="flex flex-col gap-0.5">
                      {j.assigned_staff.slice(0, 3).map((s, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                          {s.name || s.email || s.user_id}
                        </div>
                      ))}
                      {j.assigned_staff.length > 3 && <span className="text-muted-foreground">+{j.assigned_staff.length - 3} more</span>}
                    </div>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-right">${(j.estimated_total || 0).toFixed(2)}</td>
                <td className="px-3 py-2"><Badge className="text-[10px]">{j.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// shared bits
// ===========================================================================
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

/**
 * Editable inline estimate input for live auto-rows. Saves on blur or Enter.
 */
function LiveTargetInput({ targetKey, valueCents, onSave }) {
  const [val, setVal] = useState(((valueCents || 0) / 100).toString());
  const [saving, setSaving] = useState(false);
  // Keep in sync if backend value changes externally
  useEffect(() => { setVal(((valueCents || 0) / 100).toString()); }, [valueCents]);

  const commit = async () => {
    const cents = Math.round(parseFloat(val || 0) * 100);
    if (cents === (valueCents || 0)) return;
    setSaving(true);
    try { await onSave(targetKey, cents); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex items-center justify-end gap-1">
      <span className="text-xs text-muted-foreground">$</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={val}
        disabled={saving}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className="w-24 bg-background border border-border rounded px-2 py-1 text-xs text-right tabular-nums focus:border-amber-500 focus:outline-none"
        data-testid={`live-target-${targetKey}`}
        placeholder="0"
      />
    </div>
  );
}

const KPI_COLORS = {
  emerald: 'bg-emerald-500/10 text-emerald-400',
  rose: 'bg-rose-500/10 text-rose-400',
  indigo: 'bg-indigo-500/10 text-indigo-400',
  amber: 'bg-amber-500/10 text-amber-400',
};

function KPI({ icon: Icon, label, value, color = 'indigo', testid }) {
  return (
    <Card className="border-border">
      <CardContent className="p-3 flex items-center gap-3" data-testid={testid}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${KPI_COLORS[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
          <div className="text-base font-bold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-semibold ${accent || ''}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function MiniCount({ label, value, sub }) {
  return (
    <Card className="border-border">
      <CardContent className="p-3">
        <div className="text-[10px] text-muted-foreground uppercase">{label}</div>
        <div className="text-xl font-bold">{value}</div>
        <div className="text-[10px] text-muted-foreground">{sub}</div>
      </CardContent>
    </Card>
  );
}

/**
 * SupplierHubPicker — modal that lists suppliers from the Supplier Hub
 * (`/api/suppliers/hub`) and lets the admin click one to pre-fill the vendor form.
 */
function SupplierHubPicker({ onPick, onClose }) {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/suppliers/hub`);
        setSuppliers(res.data?.suppliers || []);
      } catch {
        toast.error('Failed to load Supplier Hub');
      } finally { setLoading(false); }
    })();
  }, []);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? suppliers.filter(s => (
        (s.business_name || '').toLowerCase().includes(q) ||
        (s.category || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q)
      ))
    : suppliers;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-[60] p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="supplier-hub-picker"
    >
      <div
        className="w-full max-w-2xl my-10 bg-card rounded-xl border border-border shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <BookmarkPlus className="h-4 w-4 text-amber-400" />
            <h3 className="text-sm font-semibold">Pick from Supplier Hub</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-hub-picker"><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-4 space-y-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, category, or email…"
              className="pl-9"
              data-testid="hub-picker-search"
            />
          </div>

          {loading ? (
            <div className="py-10 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-xs text-muted-foreground">
              {suppliers.length === 0
                ? 'No onboarded suppliers in the hub yet.'
                : 'No suppliers match your search.'}
            </div>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto divide-y divide-border border border-border rounded-lg">
              {filtered.map((s, i) => (
                <button
                  key={s.vendor_id || s.id || i}
                  type="button"
                  onClick={() => onPick(s)}
                  className="w-full text-left p-3 hover:bg-amber-500/5 transition-colors flex items-center gap-3"
                  data-testid={`hub-supplier-${s.vendor_id || i}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 text-amber-400 text-xs font-bold uppercase">
                    {(s.business_name || s.name || '?').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.business_name || s.name || 'Unnamed supplier'}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {s.category ? <span className="capitalize">{s.category}</span> : 'Uncategorized'}
                      {s.email && <> · {s.email}</>}
                    </div>
                  </div>
                  <Plus className="h-4 w-4 text-amber-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          <p className="text-[10px] text-muted-foreground">
            Manage the Supplier Hub at <a href="/admin/supplier-performance" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">Supplier Performance →</a>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * ReceiptRow — single attachment with editable amount.
 * Each receipt has its own amount; the parent line's actual_cents is
 * auto-summed by the backend (`actual_from_receipts` flag).
 */
function ReceiptRow({ attachment, lineId, idx, onDetach, onAmountChange }) {
  const [val, setVal] = useState(((attachment.amount_cents || 0) / 100).toString());
  useEffect(() => { setVal(((attachment.amount_cents || 0) / 100).toString()); }, [attachment.amount_cents]);

  const commit = () => {
    const cents = Math.round(parseFloat(val || 0) * 100);
    if (cents === (attachment.amount_cents || 0)) return;
    onAmountChange(lineId, idx, { amount_cents: cents });
  };

  return (
    <div className="flex items-center gap-1 text-[11px]" data-testid={`receipt-${lineId}-${idx}`}>
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-indigo-400 hover:underline flex items-center gap-1 truncate max-w-[120px]"
        data-testid={`attachment-${lineId}-${idx}`}
        title={attachment.filename}
      >
        <Paperclip className="h-3 w-3 flex-shrink-0" />{attachment.filename}
      </a>
      <div className="flex items-center gap-0.5 ml-auto">
        <span className="text-muted-foreground">$</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-16 bg-background border border-border rounded px-1 py-0.5 text-[10px] text-right tabular-nums focus:border-emerald-500 focus:outline-none"
          data-testid={`receipt-amount-${lineId}-${idx}`}
          placeholder="0"
        />
      </div>
      <button
        type="button"
        onClick={() => onDetach(lineId, idx)}
        className="text-rose-400 hover:text-rose-300 flex-shrink-0"
        data-testid={`detach-${lineId}-${idx}`}
        title="Remove receipt"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

/**
 * TicketForecastModal — per-ticket-type forecast editor.
 * Loads breakdown from /budget-targets (seeded from real ticket types) and
 * lets admin set qty + unit price per type. Total auto-rolls to
 * tickets_estimated_cents.
 */
function TicketForecastModal({ eventId, auth, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/events/${eventId}/production/budget-targets`, auth);
        setRows(res.data?.tickets_breakdown || []);
      } catch {
        toast.error('Failed to load ticket forecast');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const total = rows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price_cents) || 0), 0);

  const updateRow = (i, patch) => {
    setRows(rs => rs.map((r, ri) => ri === i ? { ...r, ...patch } : r));
  };

  const addRow = () => {
    setRows(rs => [...rs, { ticket_type_id: '', name: 'Custom tier', qty: 0, unit_price_cents: 0 }]);
  };

  const removeRow = (i) => {
    setRows(rs => rs.filter((_, ri) => ri !== i));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/events/${eventId}/production/budget-targets`, { tickets_breakdown: rows }, auth);
      toast.success(`Ticket forecast saved · ${fmt(total)}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="ticket-forecast-modal">
      <div className="w-full max-w-2xl my-10 bg-card rounded-xl border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-emerald-400" />
            <h3 className="text-sm font-semibold">Ticket sales forecast</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-forecast"><X className="h-4 w-4" /></Button>
        </div>

        {loading ? (
          <div className="py-10 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-400" /></div>
        ) : (
          <div className="p-4 space-y-3">
            <p className="text-[11px] text-muted-foreground">
              Estimate quantity sold and the price you'll charge for each ticket type. The total flows into your Pro-forma Budget income forecast so you can model break-even scenarios.
            </p>

            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-xs">
                    <th className="px-3 py-2 text-left">Ticket type</th>
                    <th className="px-3 py-2 text-right w-24">Qty est.</th>
                    <th className="px-3 py-2 text-right w-28">Unit price</th>
                    <th className="px-3 py-2 text-right w-24">Subtotal</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan="5" className="px-3 py-6 text-center text-xs text-muted-foreground">No ticket types yet. Add your first one below.</td></tr>
                  ) : rows.map((r, i) => {
                    const sub = (Number(r.qty) || 0) * (Number(r.unit_price_cents) || 0);
                    return (
                      <tr key={i} className="border-t border-border" data-testid={`forecast-row-${i}`}>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={r.name || ''}
                            onChange={(e) => updateRow(i, { name: e.target.value })}
                            className="w-full bg-background border border-border rounded px-2 py-1 text-xs"
                            placeholder="Tier name"
                            data-testid={`forecast-name-${i}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number" min="0" step="1"
                            value={r.qty || 0}
                            onChange={(e) => updateRow(i, { qty: parseInt(e.target.value || 0, 10) })}
                            className="w-20 bg-background border border-border rounded px-2 py-1 text-xs text-right tabular-nums"
                            data-testid={`forecast-qty-${i}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            <span className="text-xs text-muted-foreground">$</span>
                            <input
                              type="number" min="0" step="0.01"
                              value={(r.unit_price_cents || 0) / 100}
                              onChange={(e) => updateRow(i, { unit_price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                              className="w-20 bg-background border border-border rounded px-2 py-1 text-xs text-right tabular-nums"
                              data-testid={`forecast-price-${i}`}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right text-xs font-medium tabular-nums" data-testid={`forecast-sub-${i}`}>
                          {fmt(sub)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button type="button" onClick={() => removeRow(i)} className="text-rose-400 hover:text-rose-300" data-testid={`forecast-remove-${i}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border bg-muted/30">
                    <td colSpan="3" className="px-3 py-2 text-right text-xs font-semibold">Forecast total</td>
                    <td className="px-3 py-2 text-right text-sm font-bold text-emerald-400 tabular-nums" data-testid="forecast-total">{fmt(total)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" size="sm" onClick={addRow} data-testid="forecast-add-row">
                <Plus className="h-3.5 w-3.5 mr-1" />Add tier
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
                <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-forecast">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Save forecast
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
